import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { IFileManagementService, ILoggerService } from '../../services';
import { Directory, ComponentDefinition, Group, ScriptType, IScript, HierarchicalDirectory, IPreselectedFileType } from '../../models';
import { IRootScope } from '../../IRootScope';
import * as events from '../../common/events';

class FileListController implements angular.IComponentController, angular.IOnInit {
    canCollapseAll: boolean;
    canExpandAll: boolean;
    isEditMode: boolean;
    isLoading: boolean;
    items: HierarchicalDirectory[];
    loadingMessage: string;
    preSelect: IPreselectedFileType;
    script: IScript;
    selectedItem: Directory;

    private _continueMessage: string;
    private reloadGroups: () => angular.IPromise<Group[]>;

    static $inject = [
        '$scope',
        '$rootScope',
        '$window',
        '$timeout',
        '$translate',
        'fileManagementService',
        'loggerService',
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$window: angular.IWindowService,
        private _$timeout: angular.ITimeoutService,
        private _$translate: angular.translate.ITranslateService,
        private _fileManagementService: IFileManagementService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this.refresh();
        this.isEditMode = this._$rootScope.isEditMode;
        this.canCollapseAll = true;

        const timeout = this._$timeout(() => {
            this._continueMessage = this._$translate.instant('Save or cancel changes before selecting another file');
            this._$timeout.cancel(timeout);
        });

        this._$scope.$on(events.toggleEditMode, () => {
            this.isEditMode = this._$rootScope.isEditMode;
        });
    }

    private refresh() {
        this.items = [];
        this.loadingMessage = 'Loading files tree';
        this.isLoading = true;

        this._fileManagementService
            .getStructure()
            .then((data: Directory) => {
                const hierarchy = d3.layout.tree<HierarchicalDirectory>()
                    .children(d => {
                        let result = [];
                        if (d.directories) {
                            result = result.concat(d.directories);
                        }
                        if (d.files) {
                            result = result.concat(d.files.map(f => {
                                return {
                                    name: f.name,
                                    size: f.size,
                                    objectUserCollection: f.objectUserCollection
                                };
                            }));
                        }
                        return result as HierarchicalDirectory[];
                    });
                const tree = hierarchy(data as HierarchicalDirectory);
                const parentName = this.getLibraryFolder(this.preSelect.type);
                tree.forEach((t, i) => {
                    t.id = `id${i}`;
                    t.isVisible = true;
                    if (t.name === this.preSelect.file && t?.parent?.name === parentName) {
                        this.selectedItem = t;
                    }
                });
                this.items = tree;
                this.isLoading = false;
            })
            .catch(() => {
                this.isLoading = false;
            });
    }

    style(item) {
        return {
            'padding-left': (item.depth) * 20 + 'px'
        };
    }

    selectItem(item) {
        if (this.script && this.script.active && this.script.formCtrl.$dirty) {
            this._loggerService.logWarning(this._continueMessage);
        } else {
            this.selectedItem = item;
        }
    }

    deleteItem(item) {
        this.loadingMessage = 'Deleting file';
        this.isLoading = true;
        this._fileManagementService.deleteFile(item.parent.relativePath, item.name)
            .then(() => {
                this.selectedItem = null;
                this.refresh();
            })
            .catch(() => this.isLoading = false);
    }

    download(item) {
        this.loadingMessage = 'Downloading file';
        this.isLoading = true;
        this._fileManagementService.download(item.parent ? item.parent.relativePath : '../', item.name)
            .then((data) => {
                const url = data;
                const fileName = item.name + '.zip';
                const a: HTMLAnchorElement = this._$window.document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.target = '_blank';
                // Append anchor to body.
                document.body.appendChild(a);
                a.click();

                //// Remove anchor from body
                document.body.removeChild(a);
                this.isLoading = false;
            })
            .catch(() => this.isLoading = false);

    }

    ondrop(files: FileList, binId: string): void {
        this.loadingMessage = 'Uploading files';
        this.isLoading = true;
        const item = this.items.find(d => d.id === binId);
        if (item) {
            this._fileManagementService
                .upload(files[0], item.relativePath)
                .then(() => {
                    this.refresh();
                    this.reloadGroups();
                })
                .catch(reason => {
                    this._loggerService.logError('File cannot be uploaded', true, '', reason);
                })
                .finally(() => this.isLoading = false);
        }
    }


    toggleChildren(item, $event: angular.IAngularEvent): void {
        if (item.children) {
            this.collapseItem(item);
        } else {
            this.expandItem(item);
        }
        $event.preventDefault();
        $event.stopPropagation();
    }

    private changeVisibility(item, isVisible: boolean) {
        if (item.children) {
            item.children.forEach(c => {
                c.isVisible = isVisible;
                this.changeVisibility(c, isVisible);
            });
        }
    }

    expandAll() {
        const level0 = _.filter(this.items, d => d.depth === 1);
        level0.forEach(i => {
            this.expandTree(i);
        });
        this.canExpandAll = false;
    }

    collapseAll() {
        const level0 = _.filter(this.items, d => d.depth === 1);
        level0.forEach(i => this.collapseTree(i));
        this.canCollapseAll = false;
    }

    humanFileSize(size: number) {
        const i = Math.floor(Math.log(size) / Math.log(1024));
        return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
    }

    setReadOnlyMode() {
        this._$rootScope.$broadcast(events.toggleEditMode, false);
    }

    private expandTree(item) {
        this.expandItem(item);
        if (item.children) {
            item.children.forEach(c => this.expandTree(c));
        }
    }

    private collapseTree(item) {
        if (item.children) {
            item.children.forEach(c => this.collapseTree(c));
        }
        this.collapseItem(item);
    }

    private expandItem(item) {
        if (item._children) {
            item.children = item._children;
            this.changeVisibility(item, true);
            item._children = null;
        }
        this.canCollapseAll = true;
    }

    private collapseItem(item) {
        if (item.children) {
            this.changeVisibility(item, false);
            item._children = item.children;
            item.children = null;
        }
        this.canExpandAll = true;
    }

    private getLibraryFolder(type: ScriptType) {
        let result = '';
        switch (type) {
            case ScriptType.PythonV2:
                result = 'Library';
                break;
            case ScriptType.PythonV3:
                result = 'LibraryV3';
                break;
            default:
                console.warn('Unknown script type');
        }
        return result;
    }
}

export const wtFileList: ComponentDefinition = {
    componentId: 'wtFileList',
    controller: FileListController,
    template: require('./fileList.component.html'),
    controllerAs: 'vm',
    bindings: {
        preSelect: '<?',
        reloadGroups: '&',
        script: '=',
        selectedItem: '=?'
    }
};